/**
 * Toast — sanitizing wrapper around `sonner`.
 *
 * Drop-in replacement for `import { toast } from "sonner"`. Every method
 * behaves exactly like sonner's, EXCEPT `toast.error(...)`, which runs its
 * message through `getFriendlyErrorMessage` / `sanitizeErrorMessage` first.
 *
 * Why: hundreds of call sites do `toast.error(err.message)` or
 * `toast.error(String(err))`, which leaks raw Convex errors like
 * "[CONVEX M(orders/swaps:swapOrderItems)] [Request ID: …] Server Error" to
 * shop owners. Routing every error toast through here means none of that
 * technical noise ever reaches a user — without touching each call site's
 * arguments. Just swap the import path.
 */

import { toast as sonnerToast } from "sonner";
import { getFriendlyErrorMessage, sanitizeErrorMessage } from "@/lib/utils/errors";

type SonnerToast = typeof sonnerToast;

// Callable that delegates to sonner's default toast.
const toastFn = ((message: any, data?: any) => sonnerToast(message, data)) as SonnerToast;

// Copy sonner's static methods (success, info, warning, loading, promise,
// dismiss, custom, message, …) so the full API is preserved.
Object.assign(toastFn, sonnerToast);

// Override `error` to make the message user-friendly.
toastFn.error = ((message: any, data?: any) => {
  let clean: any = message;
  if (typeof message === "string") {
    clean = sanitizeErrorMessage(message);
  } else if (
    message instanceof Error ||
    (message && typeof message === "object" && "message" in message)
  ) {
    // Someone passed the raw error object straight in.
    clean = getFriendlyErrorMessage(message);
  }
  return sonnerToast.error(clean, data);
}) as SonnerToast["error"];

export const toast = toastFn;
